import { useEffect, useState } from "react";
import { Upload, Loader2, X, Star } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImage } from "@/lib/api";

export const AdminVideos = () => {
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");

  const load = () => api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const res = await api.post("/videos", { url: up.data.url, title });
      setVideos((prev) => [...prev, res.data]);
      setTitle("");
      toast.success("Video added");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/videos/${id}`);
    setVideos((prev) => prev.filter((v) => v.id !== id));
    toast.success("Video removed");
  };

  const setFeatured = async (id) => {
    setVideos((prev) => prev.map((v) => ({ ...v, featured: v.id === id })));
    try {
      await api.put(`/videos/${id}`, { featured: true });
      toast.success("Set as featured (plays big on top)");
    } catch {
      toast.error("Failed to update");
    }
  };

  const inputCls = "bg-black border border-white/15 px-4 py-3 text-white placeholder:text-white/30 focus:border-ad-red focus:outline-none transition-colors";

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-ad-surface border border-white/10 p-6 flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="flex-1">
          <label className="block text-xs font-bold uppercase tracking-widest text-white/50 mb-2">Video Title (optional)</label>
          <input className={`${inputCls} w-full`} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Showroom Tour" />
        </div>
        <label
          data-testid="upload-video-btn"
          className="inline-flex items-center gap-2 px-5 py-3 bg-ad-red text-white text-sm font-bold uppercase tracking-wider cursor-pointer hover:bg-ad-redhover transition-colors whitespace-nowrap"
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {uploading ? "Uploading…" : "Upload Video"}
          <input type="file" accept="video/*" className="hidden" onChange={handleUpload} disabled={uploading} />
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {videos.map((v) => (
          <div key={v.id} className="group relative bg-black border border-white/10 overflow-hidden" data-testid={`admin-video-${v.id}`}>
            <div className="h-64 bg-black grid place-items-center">
              <video src={resolveImage(v.url)} controls preload="metadata" className="w-full h-full object-contain" />
            </div>
            {v.title && <div className="px-3 py-2 text-sm text-white/80 border-t border-white/10">{v.title}</div>}
            {v.featured && <span className="absolute top-2 left-2 bg-ad-red text-white text-[9px] font-bold uppercase tracking-widest px-2 py-0.5">Featured</span>}
            <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => setFeatured(v.id)}
                data-testid={`feature-video-${v.id}`}
                className={`h-8 w-8 grid place-items-center transition-colors ${v.featured ? "bg-ad-red text-white" : "bg-black/70 text-white hover:bg-ad-red"}`}
                title="Set as featured (big autoplay on top)"
              >
                <Star size={15} fill={v.featured ? "currentColor" : "none"} />
              </button>
              <button
                onClick={() => remove(v.id)}
                data-testid={`remove-video-${v.id}`}
                className="h-8 w-8 grid place-items-center bg-black/70 text-white hover:bg-ad-red transition-colors"
                title="Remove"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
      {!videos.length && <p className="text-white/40 text-center py-10">No videos yet. Upload one above.</p>}
    </div>
  );
};
